import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, LogOut, RefreshCw, Search, Trash2, Phone, Loader2, ArrowLeft, Lock, StickyNote } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { WhatsAppIcon } from "@/components/landing/Icons";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const TOKEN_KEY = "hammer_admin_token";

const STATUS = {
  new: { label: "Nuova", cls: "bg-hammer-red text-bone" },
  contacted: { label: "Contattata", cls: "bg-ink text-bone" },
  archived: { label: "Archiviata", cls: "bg-concrete text-ink/70 border border-ink/30" },
};
const FILTERS = [
  { k: "all", label: "Tutte" },
  { k: "new", label: "Nuove" },
  { k: "contacted", label: "Contattate" },
  { k: "archived", label: "Archiviate" },
];

const fmtDate = (iso) => {
  try {
    return new Intl.DateTimeFormat("it-CH", { dateStyle: "short", timeStyle: "short", timeZone: "Europe/Zurich" }).format(new Date(iso));
  } catch {
    return iso;
  }
};
const waNum = (phone) => {
  let d = (phone || "").replace(/\D/g, "");
  if (d.startsWith("00")) d = d.slice(2);
  else if (d.startsWith("0")) d = "41" + d.slice(1);
  return d;
};

const Login = ({ onLogin }) => {
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (!pw) return;
    setLoading(true);
    setErr("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password: pw });
      onLogin(data.token);
    } catch (e2) {
      setErr(e2?.response?.status === 401 ? "Password errata." : "Errore di connessione. Riprova.");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="min-h-screen paper grid place-items-center px-4">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm bg-bone border-[3px] border-ink shadow-[10px_10px_0_#D7192A] p-8">
        <div className="flex items-center gap-3">
          <span className="w-11 h-11 bg-hammer-red border-2 border-ink grid place-items-center text-bone">
            <Lock className="w-5 h-5" />
          </span>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink/60">Hammer Gym</p>
            <h1 className="font-display text-4xl leading-none">Area riservata</h1>
          </div>
        </div>
        <label htmlFor="admin-pw" className="block font-mono text-[11px] uppercase tracking-[0.18em] mt-8">
          Password
        </label>
        <Input
          id="admin-pw"
          type="password"
          autoFocus
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          data-testid="admin-password-input"
          className="h-12 mt-2 rounded-none border-2 border-ink bg-bone focus-visible:ring-0 focus-visible:border-hammer-red"
        />
        {err && (
          <p className="text-sm text-hammer-red font-mono mt-2" data-testid="admin-login-error">
            {err}
          </p>
        )}
        <button type="submit" disabled={loading} data-testid="admin-login-submit-button" className="btn-brutal btn-red w-full mt-6 disabled:opacity-60">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Entra"}
        </button>
        <Link to="/" data-testid="admin-back-home" className="mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-ink/60 hover:text-ink">
          <ArrowLeft className="w-4 h-4" /> Torna al sito
        </Link>
      </form>
    </div>
  );
};

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, archived: 0 });
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [toDelete, setToDelete] = useState(null);
  const [noteRow, setNoteRow] = useState(null);
  const [noteText, setNoteText] = useState("");

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const logout = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setRows([]);
  }, []);

  const handleErr = useCallback(
    (e) => {
      if (e?.response?.status === 401) {
        toast.error("Sessione scaduta, accedi di nuovo.");
        logout();
      } else {
        toast.error(e?.response?.data?.detail || "Errore di rete");
      }
    },
    [logout]
  );

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = {};
      if (filter !== "all") params.status = filter;
      if (q.trim()) params.q = q.trim();
      const [r, s] = await Promise.all([
        axios.get(`${API}/admin/trials`, { headers, params }),
        axios.get(`${API}/admin/stats`, { headers }),
      ]);
      setRows(r.data);
      setStats(s.data);
    } catch (e) {
      handleErr(e);
    } finally {
      setLoading(false);
    }
  }, [token, filter, q, headers, handleErr]);

  useEffect(() => {
    const id = setTimeout(load, q ? 300 : 0);
    return () => clearTimeout(id);
  }, [load, q]);

  const onLogin = (tk) => {
    sessionStorage.setItem(TOKEN_KEY, tk);
    setToken(tk);
  };

  const updateRow = async (id, patch) => {
    try {
      const { data } = await axios.patch(`${API}/admin/trials/${id}`, patch, { headers });
      setRows((rs) => rs.map((r) => (r.id === id ? data : r)).filter((r) => filter === "all" || r.status === filter));
      const s = await axios.get(`${API}/admin/stats`, { headers });
      setStats(s.data);
      toast.success("Aggiornato");
      return true;
    } catch (e) {
      handleErr(e);
      return false;
    }
  };

  const doDelete = async () => {
    if (!toDelete) return;
    try {
      await axios.delete(`${API}/admin/trials/${toDelete.id}`, { headers });
      setRows((rs) => rs.filter((r) => r.id !== toDelete.id));
      const s = await axios.get(`${API}/admin/stats`, { headers });
      setStats(s.data);
      toast.success("Richiesta eliminata");
    } catch (e) {
      handleErr(e);
    } finally {
      setToDelete(null);
    }
  };

  const exportCsv = () => {
    const cols = ["created_at", "status", "first_name", "last_name", "phone", "email", "goal", "preferred_time", "language", "message", "notes"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [cols.join(";"), ...rows.map((r) => cols.map((c) => esc(c === "created_at" ? fmtDate(r[c]) : r[c])).join(";"))].join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `hammer-gym-prove-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!token) return <Login onLogin={onLogin} />;

  return (
    <div className="min-h-screen paper text-ink" data-testid="admin-dashboard">
      <header className="bg-ink text-bone border-b-4 border-hammer-red">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 bg-hammer-red border-2 border-bone grid place-items-center font-display text-2xl pt-1">H</span>
            <div className="leading-none">
              <p className="font-mono text-[10px] tracking-[0.3em] text-bone/60">AREA RISERVATA</p>
              <p className="font-display text-2xl">Richieste prova gratuita</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" data-testid="admin-site-link" className="hidden sm:inline-flex h-10 items-center px-3 border-2 border-bone/40 hover:border-bone font-mono text-xs uppercase tracking-widest">
              Sito
            </Link>
            <button onClick={logout} data-testid="admin-logout-button" className="h-10 inline-flex items-center gap-2 px-3 bg-hammer-red border-2 border-bone font-mono text-xs uppercase tracking-widest">
              <LogOut className="w-4 h-4" /> Esci
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 border-2 border-ink bg-bone shadow-[6px_6px_0_#0F0F10]" data-testid="admin-stats">
          {[
            ["Totale", stats.total],
            ["Nuove", stats.new],
            ["Contattate", stats.contacted],
            ["Archiviate", stats.archived],
          ].map(([l, v], i) => (
            <div key={l} className={`p-4 ${i % 2 ? "border-l-2" : ""} ${i >= 2 ? "border-t-2 lg:border-t-0" : ""} ${i === 2 ? "lg:border-l-2" : ""} border-ink`}>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">{l}</p>
              <p className={`big-num text-5xl mt-1 ${i === 1 ? "text-hammer-red" : ""}`} data-testid={`admin-stat-${i}`}>
                {v}
              </p>
            </div>
          ))}
        </div>

        {/* toolbar */}
        <div className="mt-8 flex flex-col lg:flex-row gap-4 lg:items-center justify-between">
          <div className="flex flex-wrap gap-2" data-testid="admin-filters">
            {FILTERS.map((f) => (
              <button
                key={f.k}
                onClick={() => setFilter(f.k)}
                data-testid={`admin-filter-${f.k}`}
                className={`h-10 px-4 border-2 border-ink font-mono text-xs uppercase tracking-widest transition-colors ${
                  filter === f.k ? "bg-ink text-bone" : "bg-bone hover:bg-concrete"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <div className="relative flex-1 lg:w-72">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink/50" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Cerca nome, telefono, email"
                data-testid="admin-search-input"
                className="h-10 pl-9 rounded-none border-2 border-ink bg-bone focus-visible:ring-0 focus-visible:border-hammer-red"
              />
            </div>
            <button onClick={load} data-testid="admin-refresh-button" aria-label="Aggiorna" className="h-10 w-10 grid place-items-center border-2 border-ink bg-bone hover:bg-concrete">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button onClick={exportCsv} disabled={!rows.length} data-testid="admin-export-csv-button" className="h-10 inline-flex items-center gap-2 px-3 border-2 border-ink bg-hammer-red text-bone font-mono text-xs uppercase tracking-widest disabled:opacity-50">
              <Download className="w-4 h-4" /> CSV
            </button>
          </div>
        </div>

        {/* table */}
        <div className="mt-6 border-2 border-ink bg-bone overflow-x-auto">
          <Table data-testid="admin-leads-table">
            <TableHeader>
              <TableRow className="bg-ink hover:bg-ink border-ink">
                {["Data", "Nome", "Contatti", "Obiettivo / Orario", "Messaggio", "Stato", ""].map((h) => (
                  <TableHead key={h} className="text-bone font-mono text-[11px] uppercase tracking-widest whitespace-nowrap">
                    {h}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && !rows.length ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-14" data-testid="admin-empty-state">
                    <p className="font-display text-3xl">Nessuna richiesta</p>
                    <p className="text-sm text-ink/60 mt-1">Le nuove richieste di prova gratuita appariranno qui.</p>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r) => (
                  <TableRow key={r.id} data-testid={`admin-row-${r.id}`} className="border-ink/20 align-top">
                    <TableCell className="font-mono text-xs whitespace-nowrap">{fmtDate(r.created_at)}</TableCell>
                    <TableCell>
                      <p className="font-semibold">{r.first_name} {r.last_name}</p>
                      <p className="font-mono text-[10px] uppercase text-ink/50">{r.language}</p>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <a href={`tel:${r.phone}`} className="font-mono text-sm hover:text-hammer-red" data-testid={`admin-row-phone-${r.id}`}>
                          {r.phone}
                        </a>
                        <a href={`tel:${r.phone}`} aria-label="Chiama" className="w-7 h-7 grid place-items-center border border-ink hover:bg-ink hover:text-bone">
                          <Phone className="w-3.5 h-3.5" />
                        </a>
                        <a href={`https://wa.me/${waNum(r.phone)}`} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="w-7 h-7 grid place-items-center border border-ink hover:bg-whatsapp">
                          <WhatsAppIcon className="w-3.5 h-3.5" />
                        </a>
                      </div>
                      {r.email && <a href={`mailto:${r.email}`} className="block text-xs text-ink/70 mt-1 hover:text-hammer-red">{r.email}</a>}
                    </TableCell>
                    <TableCell className="text-sm">
                      <p>{r.goal || "—"}</p>
                      <p className="text-xs text-ink/60">{r.preferred_time}</p>
                    </TableCell>
                    <TableCell className="text-sm max-w-[260px]">
                      <p className="line-clamp-3 text-ink/80">{r.message || "—"}</p>
                      {r.notes && <p className="mt-1 text-xs font-hand text-hammer-red">Nota: {r.notes}</p>}
                    </TableCell>
                    <TableCell>
                      <Select value={r.status} onValueChange={(v) => updateRow(r.id, { status: v })}>
                        <SelectTrigger data-testid={`admin-status-select-${r.id}`} className={`h-8 w-[130px] rounded-none border-0 font-mono text-[11px] uppercase tracking-wider ${STATUS[r.status]?.cls}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="rounded-none border-2 border-ink bg-bone">
                          {Object.entries(STATUS).map(([k, v]) => (
                            <SelectItem key={k} value={k} data-testid={`admin-status-option-${k}`} className="rounded-none font-mono text-xs uppercase">
                              {v.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1.5">
                        <button
                          onClick={() => {
                            setNoteRow(r);
                            setNoteText(r.notes || "");
                          }}
                          data-testid={`admin-note-button-${r.id}`}
                          aria-label="Nota"
                          className="w-8 h-8 grid place-items-center border-2 border-ink hover:bg-ink hover:text-bone"
                        >
                          <StickyNote className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setToDelete(r)}
                          data-testid={`admin-delete-button-${r.id}`}
                          aria-label="Elimina"
                          className="w-8 h-8 grid place-items-center border-2 border-ink hover:bg-hammer-red hover:text-bone hover:border-hammer-red"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </main>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="rounded-none border-[3px] border-ink bg-bone" data-testid="admin-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-3xl">Eliminare la richiesta?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete?.first_name} {toDelete?.last_name} — {toDelete?.phone}. L'operazione non si può annullare.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-delete-cancel" className="rounded-none border-2 border-ink">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="admin-delete-confirm" onClick={doDelete} className="rounded-none bg-hammer-red hover:bg-burgundy text-bone">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!noteRow} onOpenChange={(o) => !o && setNoteRow(null)}>
        <DialogContent className="rounded-none border-[3px] border-ink bg-bone" data-testid="admin-note-dialog">
          <DialogTitle className="font-display text-3xl">Nota interna</DialogTitle>
          <DialogDescription>
            {noteRow?.first_name} {noteRow?.last_name}
          </DialogDescription>
          <Textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            rows={4}
            maxLength={2000}
            data-testid="admin-note-input"
            placeholder="Es. richiamare lunedì sera"
            className="rounded-none border-2 border-ink bg-bone focus-visible:ring-0 focus-visible:border-hammer-red"
          />
          <button
            data-testid="admin-note-save"
            onClick={async () => {
              const ok = await updateRow(noteRow.id, { notes: noteText });
              if (ok) setNoteRow(null);
            }}
            className="btn-brutal btn-ink"
          >
            Salva nota
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
